/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose, Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../fragments'
import moment from 'moment'

import {
  CaseSidebar,
  CaseEdit,
  CaseActivity,
  Spinner,
  CaseCommunicationAdd,
  Modal,
  IdentityNumberFinder,
  Button,
  SvgIcon,
  Warning,
  BulkFileUploader,
  CaseActionsSidebar as ActionsSidebar
} from '../components'

import {
  fireEvent,
  decodeBase64,
  getNodeTypeFromBase64Id,
  readablePhoneNumber
} from '../utils'

import {
  CONTENTTYPE_EMAIL,
  CONTENTTYPE_MESSAGE,
  FILETYPE_MANUAL_UPLOAD
} from '../constants'


class Case extends React.PureComponent {
  static propTypes = {
    id: PropTypes.string.isRequired,
    className: PropTypes.string,
    data: PropTypes.object
  }

  static defaultProps = {
    className: ''
  }

  constructor(props) {
    super(props)

    this.state = {
      caseId: props.match
        ? props.match.params.id
        : props.id,
      reassign: false,
      caseNote: '',
      entryAdded: null,
      assignNumberToIdentity: null,
      assignNumberToMessage: null,
      showAddNoteModal: false,
      showSendSmsModal: false,
      showUploadFilesModal: false,
      sidebarRefreshTimestamp: moment().unix()   // 'x' format = unix timestamp (seconds)
    }

    this.assignNumberRef = React.createRef()
  }

  componentDidMount() {
    fireEvent("viewed", {}, "case", decodeBase64(this.state.caseId))
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.id !== this.props.id) {
      this.setState({ caseId: this.props.id })
    }
  }

  handleClickOutside = (e) => {
    if (this.assignNumberRef.current && this.assignNumberRef.current.contains(e.target)) {
      // click is inside modal, stays open
      return
    }

    // click is outside, close modal
    this.setState({ assignNumberToIdentity: null })
  }

  saveCase = (fieldsToSave) => {
    let id = { id: this.state.caseId }
    let input = Object.assign(id, fieldsToSave)

    this.props.saveCase({ variables: { input } })
    .then(() => {
      console.log("Changes to case product/request type saved successfully")
      this.setState({ entryAdded: moment().unix() })
    })
    .catch((error) => console.log("Saving case product/request type changes failed!", error))
  }

  saveCaseNote = (message) => {
    let input = {
      case: this.state.caseId,
      note: message,
      author: localStorage.getItem("userUUID"),
      direction: "internal"
    }
    this.props.saveCaseNote({ variables: { input } })
    .then(({ data }) => {
      console.log("Case note saved successfully")
      this.setState({ entryAdded: data.casenoteMutate.casenote.id })
    })
    .catch((error) => console.log('Case note save failed!', error))
  }

  addCaseRelation = (messageId) => {
    let contentTypeModel = getNodeTypeFromBase64Id(messageId)

    let input = {
      case: this.state.caseId,
      contentType: contentTypeModel === 'MessageNode' ? CONTENTTYPE_MESSAGE : CONTENTTYPE_EMAIL,
      objectId: decodeBase64(messageId)
    }
    this.props.saveCaseRelation({ variables: { input } })
    .then(() => {
      console.log('Case relation saved successfully')
      this.setState({ entryAdded: messageId })
    })
    .catch(() => console.log('Failed to save case relation'))
  }

  handleActionClick = (e) => this.setState({ [`show${e.target.id}Modal`]: true })

  showAnyTriggeredModals = (caseAffectsId) => {
    if (this.state.assignNumberToIdentity) {
      return (
        <AssignNumberToIdentityModal
          forwardedRef={this.assignNumberRef}
          number={this.state.assignNumberToIdentity}
          message={this.state.assignNumberToMessage}
          onAssignSuccess={() => setTimeout(() => this.setState({ assignNumberToIdentity: false }), 2000)}
          onAssignFail={() => setTimeout(() => this.setState({ assignNumberToIdentity: false }), 2000)}
        />
      )
    }

    if (this.state.showAddNoteModal) {
      return (
        <Modal
          body={
            <CaseCommunicationAdd
              case={this.state.caseId}
              affectsIdentity={this.props.data.case.affects.id}
              onAdd={caseNoteMessage => {
                this.saveCaseNote(caseNoteMessage)
                this.setState({ showAddNoteModal: false })
              }}
              disabled={this.props.data.case.status === 'RESOLVED'}
              channel='casenote'
            />
          }
          closeButton={true}
          onCloseClick={() => this.setState({ showAddNoteModal: false })}
        />
      )
    }

    if (this.state.showSendSmsModal) {
      return (
        <Modal
          body={
            <CaseCommunicationAdd
              case={this.state.caseId}
              affectsIdentity={this.props.data.case.affects.id}
              onAdd={messageId => {
                this.addCaseRelation(messageId)
                this.setState({ showSendSmsModal: false })
              }}
              disabled={this.props.data.case.status === 'RESOLVED'}
              channel='sms'
            />
          }
          closeButton={true}
          onCloseClick={() => this.setState({ showSendSmsModal: false })}
        />
      )
    }

    if (this.state.showUploadFilesModal) {
      return (
        <Modal
          body={
            <BulkFileUploader
              heading='Upload files'
              className='slds-m-around_large'
              onCancel={() => this.setState({ showUploadFilesModal: false })}
              fileType={FILETYPE_MANUAL_UPLOAD}
              relationships={[
                ['case', decodeBase64(this.state.caseId)],
                ['identity', decodeBase64(caseAffectsId)]
              ]}
              onSuccess={() => (
                this.setState({ sidebarRefreshTimestamp: moment.unix() })
              )}
            />
          }
          closeButton={true}
          onCloseClick={() => this.setState({ showUploadFilesModal: false })}
        />
      )
    }
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner className="slds-is-relative" />
    }

    if (this.props.data.error) {
      return <div>An error occurred attempting to load case data.</div>
    }

    let caseRecord = this.props.data.case
    if (caseRecord) {
      return (
        <div className={this.props.className}>
          {/* header */}
          <CaseEdit
            caseId={this.state.caseId}
            caseData={caseRecord}
            reassign={this.state.reassign}
            onReassignClick={() => this.setState({ reassign: true })}
            onClickOnOtherToolbarItem={() => this.setState({ reassign: false })}
            nextActionAt={caseRecord.nextActionAt}
            className='slds-size_2-of-3'
            key="CaseEdit_header"
          />
          <div
            className='slds-grid'
            style={this.state.reassign
              ? null
              : { height: 'calc(100vh - 294px)' }  // component height is viewport height minus combined height of components above
            }>
            {/* lefthand sidebar */}
            <CaseSidebar
              caseId={this.state.caseId}
              caseAffectsIdentity={caseRecord.affects}
              caseProductType={caseRecord.productType}
              caseRequestType={caseRecord.requestType}
              caseReviewed={caseRecord.reviewed}
              saveCase={this.saveCase}
              className='slds-size_1-of-5 u-bg-color-white'
              key={`relatedItemsSidebar_${this.state.sidebarRefreshTimestamp}`}
            />
            <div className='slds-size_8-of-12'>
              {this.state.reassign &&
                <CaseEdit
                  caseId={this.state.caseId}
                  caseData={caseRecord}
                  form="reassign"
                  onSave={() => this.setState({
                    reassign: false,
                    entryAdded: moment().format('x')
                  })}
                  onCancel={() => this.setState({ reassign: false })}
                  onFail={() => this.setState({ reassign: false })}
                  nextActionAt={caseRecord.nextActionAt}
                  key="CaseEdit_reassign"
                />
              }
              {/* middle section */}
              <CaseActivity
                caseId={this.state.caseId}
                entryAdded={this.state.entryAdded}
                onAdd={this.addCaseRelation}
                assignNumberToUnknownSender={(number, message) => this.setState({ assignNumberToIdentity: number, assignNumberToMessage: message })}
                className='slds-scrollable_y'
              />
            </div>
            {/* righthand sidebar */}
            <ActionsSidebar onClick={this.handleActionClick} />
          </div>
          {this.showAnyTriggeredModals(caseRecord.affects.id)}
        </div>
      )
    } else {
      return <div>No matching case found.</div>
    }
  }
}

const MUTATION_ASSIGN_NUMBER_TO_IDENTITY = gql`
  mutation addressMutate($input: AddressMutationInput!) {
    addressMutate(input: $input) {
      address {
        id
        addressValue
        identity {
          id
        }
      }
    }
  }
`

const MUTATION_FROM_IDENTITY_ON_MESSAGE = gql`
  mutation messageMutate($input: MessageMutationInput!) {
    messageMutate(input: $input) {
      message {
        id
        fromIdentity {
          id
          firstName
          lastName
        }
      }
    }
  }
`

class AssignNumberToIdentityModal extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      assignSuccess: null,  // null if assigning number to identity not attempted, and true/false depending on assignment success
      fromIdentitySetOnMessageSuccess: null  // same principle valid as explained in comment above
    }
  }

  render() {
    let { forwardedRef: ref, number, message, onAssignSuccess, onAssignFail } = this.props
    return (
      <Modal
        ref={ref}
        heading='Assign unknown number to an identity'
        body={
          this.state.assignSuccess
            ? <Mutation mutation={MUTATION_FROM_IDENTITY_ON_MESSAGE}>
                {(messageMutate, { data }) => {
                  messageMutate({
                    variables: {
                      input: {
                        id: message,
                        fromIdentity: this.state.assignedToIdentity
                      }
                    }
                  })
                  .then(() => this.setState({ fromIdentitySetOnMessageSuccess: true }, onAssignSuccess))
                  .catch(() => this.setState({ fromIdentitySetOnMessageSuccess: false }, onAssignFail))

                  return (
                    <div className='slds-align_absolute-center'>
                      <div className='u-font-size-larger slds-size_5-of-6'>
                        <div>
                          <SvgIcon
                            variant='utility'
                            symbol='check'
                            colour='success'
                            size='small'
                            className='slds-m-right_small'
                          />
                          Number successfully assigned to identity.
                        </div>
                        <div className='slds-m-top_medium'>
                          {this.state.fromIdentitySetOnMessageSuccess
                            ? <React.Fragment>
                                <SvgIcon
                                  variant='utility'
                                  symbol='check'
                                  colour='success'
                                  size='small'
                                  className='slds-m-right_small'
                                />
                                Identity set on message successfully.
                              </React.Fragment>
                            : this.state.fromIdentitySetOnMessageSuccess === false
                              ? <Warning>Setting identity on message failed.</Warning>
                              : 'Identity now being set on the message...'
                          }
                        </div>
                      </div>
                    </div>
                  )
                }}
              </Mutation>
            : this.state.assignSuccess === false
              ? <div className='slds-align_absolute-center'>
                  <Warning className='u-font-size-larger'>Number assignment failed.</Warning>
                </div>
              : <Mutation mutation={MUTATION_ASSIGN_NUMBER_TO_IDENTITY}>
                  {(addressMutate, { data }) => (
                    <IdentityNumberFinder
                      numberFoundAdditionalRender={identity => (
                        <Button
                          type='brand'
                          onClick={() => {
                            addressMutate({
                              variables: {
                                input: {
                                  identity: identity.id,
                                  addressType: 'msisdn',
                                  addressValue: number
                                }
                              }
                            })
                            .then(() => this.setState({ assignSuccess: true, assignedToIdentity: identity.id }))
                            .catch(() => this.setState({ assignSuccess: false }, onAssignFail))
                          }}>
                          {`Assign ${readablePhoneNumber(number)} to ${identity.firstName}`}
                        </Button>
                      )}
                    />
                  )}
                </Mutation>
        }
      />
    )
  }
}

const getCase = gql`
  query($id: ID!) {
    case(id: $id) {
      id
      priority
      nextActionAt
      status
      productType
      requestType
      reviewed
      assignedTo {
        id
        firstName
        lastName
      }
      affects {
        id
        firstName
        client {
          id
        }
      }
    }
  }
`

const saveCase = gql`
  mutation($input: CaseMutationInput!) {
    caseMutate(input: $input) {
      case {
        id
        priority
        nextActionAt
        status
        productType
        requestType
        assignedTo {
          id
          firstName
          lastName
        }
      }
    }
  }
`

const saveCaseNote = gql`
  mutation saveCaseNote($input: CaseNoteMutationInput!) {
    casenoteMutate(input: $input) {
      casenote {
        ...CaseNote
      }
    }
  }
  ${fragments.casenote}
`

const saveCaseRelation = gql`
  mutation saveCaseRelation($input: CaseRelationMutationInput!) {
    caserelationMutate(input: $input) {
      caserelation {
        id
        case {
          id
        }
      }
    }
  }
`

const withData =
  graphql(getCase, {
    options: ({ match, id }) => ({
      variables: {
        id: match
          ? match.params.id
          : id
      }
    })
  })

const withCaseMutate =
  graphql(saveCase, { name: "saveCase"})

const withCaseNoteMutate =
  graphql(saveCaseNote, { name: "saveCaseNote" })

const withCaseRelationMutate =
  graphql(saveCaseRelation, { name: "saveCaseRelation" })

export default compose(
  withData,
  withCaseMutate,
  withCaseNoteMutate,
  withCaseRelationMutate
)(Case)



// WEBPACK FOOTER //
// ./src/containers/Case.js